<script lang="ts" setup>
import { TimeField, TimeFieldInput } from '@/components/ui/time-field';
import { cn } from "@/lib/utils";
import { Time } from '@internationalized/date';
import type { TimeValue } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { computed } from 'vue';


/**
 * TimePicker - A composite time picker, sibling to DatePicker.
 *
 * Wraps the segmented TimeField primitive and exposes a JS Date based v-model
 * so the same form-binding pattern as DatePicker works for time-only inputs.
 * The Date's calendar components (year/month/day) are preserved when the
 * caller already passed one in; a fresh modelValue uses today's date.
 */

const props = withDefaults(
    defineProps<{
        /** The selected time value, encoded on a JS Date (two-way bindable) */
        modelValue?: Date;
        /** Whether the picker is disabled */
        disabled?: boolean;
        /** Additional CSS classes for the input wrapper */
        class?: HTMLAttributes['class'];
        /** BCP 47 locale for the segments (defaults to en-US) */
        locale?: string;
        /** Hour cycle: 12 or 24 */
        hourCycle?: 12 | 24;
        /** Whether to display seconds */
        showSeconds?: boolean;
    }>(),
    {
        modelValue: undefined,
        disabled: false,
        class: undefined,
        locale: undefined,
        hourCycle: 24,
        showSeconds: false,
    },
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: Date | undefined): void;
}>();

const granularity = computed<'minute' | 'second'>(() => (props.showSeconds ? 'second' : 'minute'));

function dateToTime(d: Date): Time {
    return new Time(d.getHours(), d.getMinutes(), d.getSeconds());
}

function applyTimeToDate(time: TimeValue, base: Date | undefined): Date {
    const d = base ? new Date(base) : new Date();
    d.setHours(time.hour, time.minute, time.second ?? 0, 0);
    return d;
}

const internalValue = computed<TimeValue | undefined>({
    get() {
        if (!props.modelValue) return undefined;
        if (Number.isNaN(props.modelValue.getTime())) return undefined;
        return dateToTime(props.modelValue);
    },
    set(newVal) {
        if (!newVal) {
            emit('update:modelValue', undefined);
            return;
        }
        emit('update:modelValue', applyTimeToDate(newVal, props.modelValue));
    },
});

const resolvedLocale = computed(() => props.locale ?? 'en-US');
</script>

<template>
    <TimeField
        v-model="internalValue"
        :locale="resolvedLocale"
        :hour-cycle="hourCycle"
        :granularity="granularity"
        :is-disabled="disabled"
        :class="cn('w-fit', props.class)"
    >
        <TimeFieldInput class="w-fit" />
    </TimeField>
</template>
